import classNames from 'classnames'
import {lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef} from 'react'
import {useSelector} from 'react-redux'
import Badge from '../../../components/core/badge'
import ContentLoader from '../../../components/core/loader'
import Tooltip from '../../../components/core/tooltip'
import modalTypes from '../../../components/pages/serverMonitoring/modalTypes.json'
import {ExpandIcon, ReminderIcon, ZoomIcon} from '../../../icons'
import {colors} from '../../../utils/colors'
import {isNewFeatureEnabled} from '../../../utils/newFeature'
import '../index.scss'
import os from '../../../data/selectOptions/os.json'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'

const LineChart = lazy(() => import('../../../components/pages/serverMonitoring/serverCharts/LineChart'))
const Treemap = lazy(() => import('../../../components/pages/serverMonitoring/serverCharts/Treemap'))

const CONFIG_FILE = ':config_file'

const transformData = (data, metricType, serverMetrics) => {
    return data
        ?.filter(item => metricType !== serverMetrics.processes_unified.value || item?.data?.[0] !== 0)
        .sort((a, b) => (b?.data?.[0] ?? 0) - (a?.data?.[0] ?? 0))
}

const ChartWrapper = memo(({
    data,
    containerRef,
    text,
    modalDataProps,
    serverResource,
    setActiveModal,
    setShowBackButton,
    isDarkMode,
    chartKey,
    handleRulesetModal,
    fetchRulesets
}) => {
    const {metricType, categories, commonMetricsInfo} = modalDataProps
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics
    const metricConfig = Object.values(serverMetrics).find(metric => metric.value === metricType)
    const chartMeta = metricConfig?.charts?.[chartKey]

    const chartData = useMemo(() => {
        const transformedData =  transformData(data, metricType, serverMetrics)
        return metricType === serverMetrics.cpu_total_usage.value ?
            transformedData?.filter(dataItem => dataItem?.name === metricConfig.charts?.[chartKey].total_cpu_name
            ) :
            transformedData
    }, [data, metricType])

    const chartCommonProps = {
        data: chartData,
        categories,
        chartKey,
        commonMetricsInfo,
        isDarkMode,
        setShowBackButton
    }

    const showExpandedChart = () => setActiveModal({
        type: modalTypes.expanded_chart,
        modal_id: chartKey,
        metric_type: metricType
    })

    const showZoomChart = () => setActiveModal({
        type: modalTypes.zoom_chart,
        modal_id: chartKey,
        metric_type: metricType
    })

    const showListViewModal = () => {
        fetchRulesets?.()
        setActiveModal({
            type: modalTypes.ruleset_list,
            modal_id: chartKey,
            metric_type: metricType
        })
    }

    if (chartData?.length === 0) {
        return
    }

    return (
        <div
            className={classNames('content-body', {
                dark: isDarkMode,
                'processed-content-body': metricType === serverMetrics.processes_unified.value
            })}
            ref={containerRef}
        >
            <Suspense fallback={<ContentLoader />}>
                {metricType === serverMetrics.processes_unified.value ? (
                    <span className='processed-chart-body'>
                        <span className='chart-actions'>
                            <span onClick={showExpandedChart}>
                                <ExpandIcon stroke={isDarkMode ? colors.colorBgFilter : colors.colorMuted} />
                            </span>
                            <span className='coming-soon-text' onClick={handleRulesetModal}>
                                <ReminderIcon width={20} height={18} stroke={isDarkMode ? colors.colorBgFilter : colors.colorMuted} />
                            </span>
                            <span onClick={showZoomChart}>
                                <ZoomIcon fill={isDarkMode ? colors.colorBgFilter : colors.colorMuted} />
                            </span>
                        </span>
                        <Treemap
                            selectedMetric={metricConfig}
                            {...chartCommonProps}
                        />
                    </span>
                ) : (
                    <LineChart
                        type={metricType}
                        title={chartMeta?.label}
                        yName={chartMeta?.yaxis_name}
                        serverResource={serverResource}
                        containerRef={containerRef}
                        {...chartCommonProps}
                        showExpandedChart={showExpandedChart}
                        showListViewModal={showListViewModal}
                    />
                )}
            </Suspense>
        </div>
    )
})

const ServerResourceMetricsList = memo(({
    metricKey,
    metricType,
    serverResource,
    isMetricsDataRetrieving,
    isResourceFetching,
    activeModal,
    setActiveModal,
    setShowBackButton,
    selectedOsOption,
    osInstructionCodes,
    setInstructionModalContent,
    toggleInstructionModal,
    showUpdateWarning,
    showDisabledMetricInfo,
    fetchRulesets
}) => {
    const {
        categories,
        commonMetricsInfo,
        groupedData,
        absoluteAdditionalMetrics
    } = useSelector(state => state.server_monitoring[metricType] || {})
    const text = useNamespaceTranslations('server-monitoring')
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics

    const {metricSources} = useSelector(state => state.server_monitoring)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const containerRef = useRef(null)
    const chartData = groupedData?.[metricKey]
    const isLoading = isMetricsDataRetrieving[metricType] || isResourceFetching

    const modalDataProps = {
        metricType,
        categories,
        commonMetricsInfo,
        groupedData,
        absoluteMetrics: absoluteAdditionalMetrics,
        isDarkMode
    }

    const handleInstructionClick = useCallback((showModal = true) => {
        const osType = selectedOsOption?.type || os.centos.type
        const osInstructions = osInstructionCodes?.[osType]
        if (osInstructions) {
            const configFileItem = osInstructions.find(item => item.type === CONFIG_FILE)
            const lastItem = osInstructions[osInstructions.length - 1]
            setInstructionModalContent({[osType]: [configFileItem, lastItem]})
        }

        setShowBackButton(showModal && activeModal?.metric_type)
        if (showModal) toggleInstructionModal()
    }, [toggleInstructionModal, osInstructionCodes, selectedOsOption, setInstructionModalContent])

    useEffect(() => {
        handleInstructionClick(false)
    }, [showUpdateWarning])

    const handleRulesetModal = () => {
        if (!isNewFeatureEnabled()) return
        fetchRulesets?.()
        setShowBackButton(false)
        setActiveModal({
            type: modalTypes.ruleset_list,
            modal_id: metricKey,
            metric_type: metricType
        })
    }

    const metricConfig = Object.values(serverMetrics).find(metric => metric.value === metricType)
    const chartMeta = metricConfig?.charts?.[metricKey]

    if (showDisabledMetricInfo) {
        return (
            <div
                key={metricKey}
                className={classNames('content-body', {
                    dark: isDarkMode,
                    'disabled-metric': true
                })}
            >
                <span className='d-flex justify-content-between'>
                    <h5 className="disabled-metric-title">{chartMeta?.label} ({chartMeta?.default_unit})</h5>
                    <span className='coming-soon-text' onClick={handleRulesetModal}>
                        <ReminderIcon width={20} height={18} stroke={isDarkMode ? colors.colorBgFilter : colors.colorMuted} />
                    </span>
                </span>
                <span className="disabled-metric-text">
                    {text.disabled_metrics_info}
                    <span className="instructions-link" onClick={handleInstructionClick}>
                        {text.configuration}
                    </span>
                    {text.restart_agent}
                    {metricType === serverMetrics.swap_usage.value &&
                                ` ${text.disabled_metrics_info_for_swap}`}
                </span>
            </div>
        )
    }

    if (!chartData) return null

    if (isLoading && activeModal?.type !== modalTypes.expanded_chart) {
        return (
            <div
                key={metricKey}
                className={classNames('loader-container', {
                    dark: isDarkMode
                })}
            >
                <ContentLoader />
            </div>
        )
    }

    const renderNetworkDescription = () => (
        <div className="network-description">
            <span>{text.network_usage_description}</span>
            {metricSources?.length > 0 && (
                <div className="sources-container">
                    {metricSources.map((source, i) => (
                        <Badge key={i} className="badge badge-primary">
                            {source}
                        </Badge>
                    ))}
                </div>
            )}
            <p>
                {text.network_usage_instructions_text}
                <span className="instructions-link" onClick={handleInstructionClick} disabled={isLoading}>
                    {text.configuration}.
                </span>
            </p>
        </div>
    )

    if (metricType === serverMetrics.network_usage_unified.value) {
        chartMeta.description = renderNetworkDescription()
    }

    return (
        <ChartWrapper
            data={chartData}
            chartKey={metricKey}
            containerRef={containerRef}
            setShowBackButton={setShowBackButton}
            serverResource={serverResource}
            modalDataProps={modalDataProps}
            text={text}
            isDarkMode={isDarkMode}
            handleRulesetModal={handleRulesetModal}
            setActiveModal={setActiveModal}
            fetchRulesets={fetchRulesets}
        />
    )
})

const PageBody = memo(({
    metricProps,
    serverResourceProps,
    modalsProps
}) => {
    const {
        filteredMetrics,
        isMetricsDataRetrieving
    } = metricProps

    const {
        serverResource,
        selectedOsOption,
        isResourceFetching
    } = serverResourceProps

    const {
        setInstructionModalContent,
        osInstructionCodes,
        activeModal,
        setActiveModal,
        toggleInstructionModal,
        showUpdateWarning,
        setShowBackButton,
        fetchRulesets
    } = modalsProps

    const hasAnyMetricData = useSelector(state =>
        filteredMetrics.some(metric => {
            const type = metric?.value
            return state.server_monitoring?.[type]?.commonMetricsInfo
        })
    )

    return (
        <div className="charts-container">
            {filteredMetrics?.flatMap(metric =>
                Object.values(metric.charts).map(chart => (
                    <ServerResourceMetricsList
                        key={`${metric.value}-${chart.chart_key}`}
                        metricType={metric.value}
                        metricKey={chart.chart_key}
                        showDisabledMetricInfo={!hasAnyMetricData}
                        serverResource={serverResource}
                        isMetricsDataRetrieving={isMetricsDataRetrieving}
                        isResourceFetching={isResourceFetching}
                        activeModal={activeModal}
                        selectedOsOption={selectedOsOption}
                        setActiveModal={setActiveModal}
                        setShowBackButton={setShowBackButton}
                        osInstructionCodes={osInstructionCodes}
                        setInstructionModalContent={setInstructionModalContent}
                        toggleInstructionModal={toggleInstructionModal}
                        showUpdateWarning={showUpdateWarning}
                        fetchRulesets={fetchRulesets}
                    />
                ))
            )}
        </div>
    )
})

export default PageBody
